import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { CommonModule, Location, NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ActivatedRoute,
  ChildrenOutletContexts,
  Data,
  NavigationEnd,
  Router,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import { CategoryConstants } from '@bp-core/src/lib/constants/categories';
import {
  ConfigModel,
  GoalModel,
  InvestmentStrategyModel,
  UserModel,
} from '@bp-core/src/lib/proto/common-message.pb';
import { GetConfigParam } from '@bp-core/src/lib/proto/config-param.pb';
import { ConfigParamGrpcServiceClient } from '@bp-core/src/lib/proto/config-param.pbsc';
import { PortalUserGrpcServiceClient } from '@bp-core/src/lib/proto/portal-user.pbsc';
import { AuthService } from '@bp-core/src/lib/services/portal/auth.service';
import { environment } from '@environment/environment';
import { fuseAnimations } from '@fuse/animations';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';

import { PortalInsuranceGrpcServiceClient } from '@bp-core/src/lib/proto/portal-insurance.pbsc';
import { FuseNavigationService } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { GrpcCoreModule } from '@ngx-grpc/core';
import { Empty } from '@ngx-grpc/well-known-types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { BpUserComponent } from 'bp/layout/common/bp-user/bp-user.component';
import { AsesorIaLoader } from 'bp/modules/asesor-ia/asesor-ia.loader';
import { BpCodes } from 'bp/shared/models/config-code.model';
import { BpHeaderConfig } from 'bp/shared/models/header-config';
import { BpHeaderService } from 'bp/shared/services/bp-header.service';
import { BpScrollHeaderService } from 'bp/shared/services/bp-scroll-header.service';
import { PortalConfigParamService } from 'bp/shared/services/portal-config-param.service';
import { PortalPushService } from 'bp/shared/services/portal-push.service';
import { UtilityService } from 'bp/shared/services/utility-service.service';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { sortGoalModelsInPlace } from '../../../../shared/utils/goal-sorting.utils';
import { BpVerticalNavigationComponent } from '../components/vertical/vertical.component';

@Component({
    selector: 'bp-layout',
    templateUrl: './bp.component.html',
    encapsulation: ViewEncapsulation.None,
    styles: [
        `
      .bg-office {
        background-color: #363636 !important;
      }
      .bg-endow {
        background-color: #ffffff !important;
      }
      .bg-normal {
        background-color: #27272a !important;
      }
    `,
    ],
    imports: [
        CommonModule,
        FuseLoadingBarComponent,
        MatButtonModule,
        RouterOutlet,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        RouterModule,
        BpUserComponent,
        NgClass,
        GrpcCoreModule,
        BpVerticalNavigationComponent,
    ],
    animations: [
        fuseAnimations,
        // Custom animation for sidebar items
        trigger('slideInStagger', [
            transition('* <=> *', [
                query(':enter', [
                    style({ transform: 'translateX(-100%)', opacity: 0 }),
                    stagger('50ms', [
                        animate('300ms ease-out', style({ transform: 'translateX(0%)', opacity: 1 })),
                    ]),
                ], { optional: true }),
            ]),
        ]),
        // Animation for individual navigation items
        trigger('slideInLeft', [
            transition(':enter', [
                style({ transform: 'translateX(-30px)', opacity: 0 }),
                animate('250ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateX(-30px)', opacity: 0 })),
            ]),
        ]),
        // Fade in animation for async content
        trigger('fadeInUp', [
            transition(':enter', [
                style({ transform: 'translateY(20px)', opacity: 0 }),
                animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateY(20px)', opacity: 0 })),
            ]),
        ]),
    ]
})
export class BpLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  /** Clave de persistencia del estado colapsado del sidebar (desktop) */
  private static readonly SIDEBAR_COLLAPSED_STORAGE_KEY = 'bp-sidebar-collapsed';

  env = environment;
  isScreenSmall: boolean;
  navigation: Navigation;
  user: UserModel | undefined;
  shortTermInvestGoal: GoalModel | undefined;
  shortDollarsInvestmentGoal: GoalModel;
  goals: GoalModel[] | undefined;
  cajaMonedaEnable = false;
  userHasVectorEntity = false;
  fileUrlBase = environment.urlApiV2 + 'api/Files/';
  landingNewUrlContactForm = environment.urlLandingNew + '/contactanos';
  config: ConfigModel;
  token = localStorage.getItem('access_token');
  headerConfig: BpHeaderConfig | null = null;
  scrollHeaderContent: TemplateRef<any> | null = null;
  /** Vistas que piden ocultar el app bar en desktop (opt-in vía BpHeaderService). */
  hideAppBarOnDesktop = false;
  /** Vistas que piden ocultar la "B" del centro del app bar (opt-in vía BpHeaderService). */
  hideAppBarLogo = false;
  /**
   * 037 BT-6959: los avisos del navegador se sincronizan desde acá y no desde `AppComponent`
   * porque este layout es el único que se instancia con sesión iniciada; `AppComponent` también
   * corre en `/auth/*`, donde todavía no hay usuario al que registrarle el navegador.
   */
  private readonly portalPushService = inject(PortalPushService);

  /** La entrada "Asesor IA" del menú solo aparece si el asesor está disponible (GetAvailability). */
  private readonly asesorIa = inject(AsesorIaLoader);
  /** Últimos datos con los que se armó el menú, para rearmarlo cuando llega la disponibilidad del asesor. */
  private ultimosDatosMenu: { newSidebarItems: string } | null = null;
  private readonly menuAsesor = effect(() => {
    this.asesorIa.disponible();
    untracked(() => this.refrescarMenuAsesor());
  });

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  routerUrl: string;
  shortTermInversSavingStrategy: InvestmentStrategyModel | undefined;
  shortTermInversSavingPlusStrategy: InvestmentStrategyModel | undefined;
  lastRouteChildConfigData: Data | undefined;
  @ViewChild('anchorElement') anchorElement: ElementRef;
  shadow: boolean = false;
  animateRoute = true;
  bgSidebar = signal<'normal' | 'office' | 'endow'>('normal');

  /**
   * Estado colapsado del sidebar (riel icon-only). Solo aplica en desktop;
   * se persiste en localStorage para sobrevivir reload (default: expandido).
   */
  readonly sidebarCollapsed = signal<boolean>(
    localStorage.getItem(BpLayoutComponent.SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true',
  );

  // Flag to track if async data has been loaded
  private _asyncDataLoaded = false;

  // Animation control properties
  showBaseNavigation = true;
  showAsyncNavigation = false;
  animationState = 'initial';

  /**
   * Constructor
   */
  constructor(
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private authService: AuthService,
    private _portalInsuranceGrpcServiceClient: PortalInsuranceGrpcServiceClient,
    private portalConfigParamService: PortalConfigParamService,
    private configParamGrpcServiceClient: ConfigParamGrpcServiceClient,
    private portalUserGrpcServiceClient: PortalUserGrpcServiceClient,
    private router: Router,
    private utilityService: UtilityService,
    private activatedRoute: ActivatedRoute,
    private bpHeaderService: BpHeaderService,
    private contexts: ChildrenOutletContexts,
    scrollHeaderService: BpScrollHeaderService,
    private location: Location,
  ) {
    bpHeaderService.headerConfig$.subscribe(config => {
      setTimeout(() => {
        this.headerConfig = config;
      }, 0);
    });

    bpHeaderService.hideAppBarOnDesktop$.subscribe(hide => {
      setTimeout(() => {
        this.hideAppBarOnDesktop = hide;
      }, 0);
    });

    bpHeaderService.hideAppBarLogo$.subscribe(hide => {
      setTimeout(() => {
        this.hideAppBarLogo = hide;
      }, 0);
    });

    // Subscribe to scroll-based header content
    scrollHeaderService.scrollContent$.subscribe(template => {
      setTimeout(() => {
        this.scrollHeaderContent = template;
      }, 0);
    });
  }
  ngAfterViewInit(): void {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          this.shadow = !entry.isIntersecting;
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(this.anchorElement.nativeElement);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  /**
   * Getter for know if is sceensmall and the route has the HamburguerBtnOnResponsive config
   */
  get showHamburguerOnDefaultLeading(): boolean {
    if (this.isScreenSmall) {
      return this.lastRouteChildConfigData?.showHamburguerBtnOnResponsive;
    } else {
      return true;
    }
  }

  /**
   * Getter que indica si estamos en el HOME (dashboard) y NO en las subvistas
   * Actividad general / Mis inversiones (que comparten el mismo layout). En el
   * Home el botón hamburguesa se oculta; en el resto de pantallas sigue activo.
   */
  get isHome(): boolean {
    const url = this.routerUrl ?? '';
    return (
      url.includes('/dashboard') &&
      !url.includes('activities') &&
      !url.includes('general-balance')
    );
  }

  /**
   * Vistas cuyo app bar móvil es transparente sobre el fondo gris y pasa a translúcido con
   * sombra al hacer scroll (`shadow`), como el Home. En el resto es blanco.
   */
  get hasTransparentAppBar(): boolean {
    const url = this.routerUrl ?? '';
    return url.includes('/dashboard') || url.startsWith('/documents') || url.startsWith('/profile');
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.router.events
      .pipe(
        // Filtra solo los eventos de finalización de navegación
        filter(event => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        // Reinicia la animación
        this.animateRoute = false;
        setTimeout(() => (this.animateRoute = true), 0);
      });
    this.getLastRouteChildConfigData();
    this.routerUrl = this.router.url;

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(val => {
      this.routerUrl = (val as NavigationEnd).url;
      this.getLastRouteChildConfigData();
    });

    // Inicializar navegación base inmediatamente
    this.initializeBaseNavigation();

    // Configurar datos asincrónicos que modificarán la navegación
    this.setupAsyncNavigationData();

    // Avisos del navegador: re-registra la suscripción existente y deja escuchando el clic.
    // No pide permiso (eso solo ocurre desde el botón del perfil).
    this.portalPushService.syncOnLoad();
  }

  /**
   * Initialize base navigation that doesn't depend on async data
   */
  private initializeBaseNavigation(): void {
    // Get the default navigation structure
    this._navigationService.get().subscribe(navigation => {
      // Set initial navigation with base items only
      this.navigation = this.filtrarMenuAsesor(navigation);
    });

    // Subscribe to media changes for immediate screen size detection
    this._fuseMediaWatcherService.onMediaChange$.pipe(take(1)).subscribe(({ matchingAliases }) => {
      // Check if the screen is small
      this.isScreenSmall = !matchingAliases.includes('md');
    });
  }

  /**
   * Setup async data and update navigation dynamically
   */
  private setupAsyncNavigationData(): void {
    // Create requests
    const cajaMonedaconfigParamRequest = new GetConfigParam();
    cajaMonedaconfigParamRequest.code = 'ENABLE-USDBOX';

    // Combine async data streams
    combineLatest([
      this.authService.userSubject$, // user
      this.configParamGrpcServiceClient.get(cajaMonedaconfigParamRequest), // cajaMonedaConfig
      this.portalUserGrpcServiceClient.getConfig(new Empty()), // config (this is for get the some footer url files)
      this._fuseMediaWatcherService.onMediaChange$, // matchingAliases
      this.portalConfigParamService.get(BpCodes.newSidebarItems),
    ])
      .pipe(
        takeUntil(this._unsubscribeAll),
        // Add debounce to prevent too frequent updates
        debounceTime(100),
        // Only emit when values actually change
        distinctUntilChanged((prev, curr) => {
          // Compare relevant properties to determine if update is needed
          return (
            prev[0]?.id === curr[0]?.id && // user id
            prev[0]?.goals?.length === curr[0]?.goals?.length && // goals count
            prev[3]?.matchingAliases?.includes('md') === curr[3]?.matchingAliases?.includes('md') // screen size
          );
        }),
      )
      .subscribe(
        ([user, cajaMonedaConfig, config, { matchingAliases }, newSidebarItems]) => {
          // Only proceed if user data is available
          if (!user) return;

          // Update component properties
          this.user = user;
          this.goals = user?.goals;
          this.config = config;
          this.isScreenSmall = !matchingAliases.includes('md');

          if (cajaMonedaConfig?.valueType == 'BOOL') {
            this.cajaMonedaEnable = cajaMonedaConfig?.value == 'true';
          }

          this.userHasVectorEntity = this.searchIfUserHasFinancialEntity('vector');

          // Update navigation with user-dependent items
          this.updateNavigationWithUserData(newSidebarItems);

          // Mark that async data has been loaded
          this._asyncDataLoaded = true;
        },
      );
  }

  /**
   * Update navigation with user-dependent data
   */
  private updateNavigationWithUserData(newSidebarItems: string): void {
    if (!this.navigation) return;
    this.ultimosDatosMenu = { newSidebarItems };

    // Reset to base navigation to avoid duplicates
    this._navigationService.get().subscribe(baseNavigation => {
      // Create a fresh copy of the base navigation
      const navigation = JSON.parse(JSON.stringify(baseNavigation));

      // BetterplanUS ya no va acá: se mudó al menú de usuario (062-documentos, FR-003).

      // Add Sociedades item - this is always added
      this.addSociedadesItem(navigation);

      // Add investment group with metas (only for desktop)
      if (!this.isScreenSmall) {
        this.addInvestmentGroup(navigation);
      }

      // Add responsive items (only for mobile)
      if (this.isScreenSmall) {
        this.addResponsiveItems(navigation);
      }

      // Add insurance contracts if available
      this.addInsuranceContracts(navigation);

      // Add Nueva Meta action button at the end (only for desktop)
      if (!this.isScreenSmall) {
        this.addNuevaMetaActionButton(navigation);
      }

      // Add badges to new sidebar items
      this.addNewBadges(navigation, newSidebarItems);

      // Update navigation with animation
      this.updateNavigationWithAnimation(this.filtrarMenuAsesor(navigation));
    });
  }

  /** Sin la entrada del asesor mientras no se sepa (o no esté) disponible para el usuario efectivo. */
  private filtrarMenuAsesor(navigation: Navigation): Navigation {
    if (this.asesorIa.disponible() === true || !navigation?.bp) return navigation;
    return { ...navigation, bp: navigation.bp.filter(item => item.id !== 'asesor-ia') };
  }

  /** Llegó (o cambió) la disponibilidad del asesor: se rearma el menú con los mismos datos. */
  private refrescarMenuAsesor(): void {
    if (this.ultimosDatosMenu) {
      this.updateNavigationWithUserData(this.ultimosDatosMenu.newSidebarItems);
    } else if (this.navigation) {
      this._navigationService.get().pipe(take(1)).subscribe(navigation => (this.navigation = this.filtrarMenuAsesor(navigation)));
    }
  }

  /**
   * Update navigation with animation effects
   */
  private updateNavigationWithAnimation(navigation: Navigation): void {
    // If this is the first async update, trigger animations
    if (!this._asyncDataLoaded) {
      // Small delay to allow base navigation to render first
      setTimeout(() => {
        this.navigation = navigation;
        this.showAsyncNavigation = true;
        this.animationState = 'loaded';
      }, 150);
    } else {
      // For subsequent updates, update directly
      this.navigation = navigation;
    }
  }

  /**
   * Add Sociedades item (only for non-entity users)
   */
  private addSociedadesItem(navigation: Navigation): void {
    if (this.user?.userType === 'entity') return;

    navigation.bp.push({
      id: 'entity',
      title: 'Sociedades',
      type: 'basic',
      icon: 'heroicons_outline:briefcase',
      link: '/entity/activate',
    });
  }

  /**
   * Add Nueva Meta action button
   */
  private addNuevaMetaActionButton(navigation: Navigation): void {
    navigation.bp.push({
      id: 'nueva-meta',
      title: 'Nueva solución',
      type: 'action-button',
      icon: 'heroicons_outline:plus',
      function: () => {
        this.utilityService.navigateToNewGoal();
      },
    });
  }

  /**
   * Add savings accounts to metas group
   */
  private addSavingsAccountsToMetas(metasGroup: any): void {
    // Search for savings goals
    this.shortTermInvestGoal = this.searchGoalByCategory('short-term-invest');
    const shortTermInvestPlusGoal = this.searchGoalByCategory('short-term-invest-plus');
    const shortTermInvestDollarGoal = this.searchGoalByCategory('short-term-invest-dollars');
    const shortTermInvestDolarPlusGoal = this.searchGoalByCategory(
      'short-term-invest-dollars-plus',
    );

    // Add Cuenta Ahorro if available and has capital
    if (this.shortTermInvestGoal && this.shortTermInvestGoal.currentCapital !== 0) {
      metasGroup.children.push({
        id: 'metas.cuenta-ahorro',
        title: 'Cuenta Ahorro',
        type: 'basic',
        // icon: 'mat_outline:savings',
        link: '/goals/' + this.shortTermInvestGoal.id,
        fragment: 'summary',
      });
    }

    // Add Cuenta Ahorro Plus if available and has capital
    if (shortTermInvestPlusGoal && shortTermInvestPlusGoal.currentCapital !== 0) {
      metasGroup.children.push({
        id: 'metas.cuenta-ahorro-plus',
        title: 'Cuenta Ahorro Plus',
        type: 'basic',
        // icon: 'mat_outline:savings',
        link: '/goals/' + shortTermInvestPlusGoal.id,
        fragment: 'summary',
      });
    }

    // Add Cuenta Ahorro Dólar if available and has capital
    if (shortTermInvestDollarGoal && shortTermInvestDollarGoal.currentCapital !== 0) {
      metasGroup.children.push({
        id: 'metas.cuenta-ahorro-dolar',
        title: 'Cuenta Ahorro Dólar',
        type: 'basic',
        // icon: 'mat_outline:savings',
        link: '/goals/' + shortTermInvestDollarGoal.id,
        fragment: 'summary',
      });
    }

    // Add Cuenta Dólar Plus if available and has capital
    if (shortTermInvestDolarPlusGoal && shortTermInvestDolarPlusGoal.currentCapital !== 0) {
      metasGroup.children.push({
        id: 'metas.cuenta-ahorro-dolar-plus',
        title: 'Cuenta Dólar Plus',
        type: 'basic',
        // icon: 'mat_outline:savings',
        link: '/goals/' + shortTermInvestDolarPlusGoal.id,
        fragment: 'summary',
      });
    }
  }

  /**
   * Add investment group items
   * Ver goal-sorting.utils.ts para documentación del ordenamiento
   */
  private addInvestmentGroup(navigation: Navigation): void {
    // Filter normal investment goals (solo las que tienen capital)
    const filteredNormalGoals = this?.goals?.filter(
      element =>
        !element.archived &&
        element?.goalCategory?.code !== CategoryConstants.wallet &&
        !CategoryConstants.savingCategoryCodes.includes(element?.goalCategory?.code!) &&
        element.currentCapital !== 0 &&
        element.financialEntity?.uuid !== 'alpaca',
    );

    // Ordenar usando las utilidades centralizadas
    // Ver goal-sorting.utils.ts para criterios de ordenamiento
    if (filteredNormalGoals) {
      sortGoalModelsInPlace(filteredNormalGoals);
    }

    // Create Metas collapsable group
    const metasGroup: any = {
      id: 'metas',
      title: 'Inversiones',
      type: 'collapsable',
      icon: 'heroicons_outline:chart-pie',
      children: [],
    };

    // Add savings accounts first if user has vector entity
    if (this.userHasVectorEntity) {
      this.addSavingsAccountsToMetas(metasGroup);
    }

    // Add regular investment goals
    if (filteredNormalGoals) {
      filteredNormalGoals.forEach(g => {
        const { icon, classes } = this.getIconOrClass(
          g.icon ? g.icon.code : g.goalCategory?.icon?.code,
        );

        metasGroup.children.push({
          id: 'metas.goal-' + g.id,
          title: g.title,
          type: 'basic',
          // icon,
          classes: { icon: classes },
          link: '/goals/' + g.id,
          fragment: 'summary',
        });
      });
    }

    // Add wallet items if enabled (excluding Alpaca wallets)
    this.addWalletItems(metasGroup);

    // Only add the Metas group if it has children
    if (metasGroup.children && metasGroup.children.length > 0) {
      navigation.bp.push(metasGroup);
    }

    // Add Inversiones group (Alpaca goals)
    this.addInversionesGroup(navigation);
  }

  /**
   * Add Inversiones group for Alpaca stock goals
   */
  private addInversionesGroup(navigation: Navigation): void {
    const alpacaGoals = this?.goals?.filter(
      element =>
        !element.archived &&
        element.financialEntity?.uuid === 'alpaca' &&
        element.currentCapital !== 0,
    );

    if (!alpacaGoals?.length) return;

    const inversionesGroup: any = {
      id: 'inversiones',
      title: 'Acciones',
      type: 'collapsable',
      icon: 'heroicons_outline:arrow-trending-up',
      children: [],
    };

    alpacaGoals.forEach(g => {
      const isBuyingPower = g.goalCategory?.code === 'caja-moneda';
      inversionesGroup.children.push({
        id: 'inversiones.goal-' + g.id,
        title: isBuyingPower ? 'Poder de Compra' : g.title,
        type: 'basic',
        link: '/stocks/' + g.id,
        fragment: 'summary',
      });
    });

    navigation.bp.push(inversionesGroup);
  }

  /**
   * Add wallet items to metas group
   */
  private addWalletItems(metasGroup: any): void {
    const cajaMonedaUSDGoal = this.searchGoalByCategoryAndCurrency('caja-moneda', 'USD');
    const cajaMonedaCLPGoal = this.searchGoalByCategoryAndCurrency('caja-moneda', 'CLP');

    // Add USD wallet if enabled, has capital, and is NOT Alpaca (Alpaca wallets go to Inversiones)
    if (
      this.cajaMonedaEnable &&
      cajaMonedaUSDGoal &&
      cajaMonedaUSDGoal.currentCapital !== 0 &&
      cajaMonedaUSDGoal.financialEntity?.uuid !== 'alpaca'
    ) {
      metasGroup.children?.push({
        id: 'metas.billetera-dolar',
        title: 'Billetera Dólar',
        type: 'basic',
        // icon: 'mat_solid:local_atm',
        link: '/goals/' + cajaMonedaUSDGoal.id,
        fragment: 'summary',
      });
    }

    // Add CLP wallet if enabled and has capital
    if (this.cajaMonedaEnable && cajaMonedaCLPGoal && cajaMonedaCLPGoal.currentCapital !== 0) {
      metasGroup.children?.push({
        id: 'metas.billetera-pesos',
        title: 'Billetera Pesos',
        type: 'basic',
        // icon: 'mat_solid:local_atm',
        link: '/goals/' + cajaMonedaCLPGoal.id,
        fragment: 'summary',
      });
    }
  }

  /**
   * Add responsive items for mobile
   */
  private addResponsiveItems(navigation: Navigation): void {
    // «Actividad» ya no va acá: es parte del menú base, después de Descubrir (062-documentos).
    navigation.bp.push(
      {
        id: 'referrals',
        title: 'Invita y gana',
        type: 'basic',
        icon: 'heroicons_outline:gift',
        function: () => {
          this.utilityService.navigateToReferral();
        },
      },
      {
        id: 'new-goal',
        title: 'Nueva solución',
        type: 'action-button',
        function: () => {
          this.utilityService.navigateToNewGoal();
        },
      },
    );
  }

  /**
   * Add insurance contracts if available
   */
  private addInsuranceContracts(navigation: Navigation): void {
    if (this.user?.insuranceContracts?.length! > 0) {
      // Add insurance contracts as individual basic items, not grouped
      this.user?.insuranceContracts?.forEach(contract => {
        navigation.bp.push({
          id: 'seguro-' + contract.id,
          title: contract.product?.title,
          type: 'basic',
          icon: 'heroicons_outline:shield-check',
          link: '/insurances/' + contract.id,
        });
      });
    }
  }

  /**
   * Add badges to new sidebar items
   */
  private addNewBadges(navigation: Navigation, newSidebarItems: string): void {
    if (newSidebarItems && newSidebarItems !== '') {
      const newSidebarItemsArray = newSidebarItems.split(',');
      const newBadge = {
        title: 'Nuevo',
        classes: 'px-2 bg-primary text-white rounded-full',
      };
      newSidebarItemsArray.forEach(item => {
        const sidebarItem = navigation.bp.find(e => e.id === item);
        if (sidebarItem) {
          sidebarItem.badge = newBadge;
        }
      });
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<BpVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /**
   * Alterna el estado colapsado del sidebar (riel icon-only) y lo persiste.
   * Solo tiene efecto visible en desktop — en mobile el sidebar es modo 'over'.
   */
  toggleSidebarCollapsed(): void {
    const next = !this.sidebarCollapsed();
    this.sidebarCollapsed.set(next);
    localStorage.setItem(BpLayoutComponent.SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
  }

  /**
   * Re-expande el sidebar completo cuando, estando colapsado, el usuario clickea
   * un item collapsable. El propio item collapsable se encarga de quedar abierto;
   * acá solo restauramos el panel y persistimos el estado.
   */
  onSidebarExpandRequested(): void {
    if (!this.sidebarCollapsed()) return;
    this.sidebarCollapsed.set(false);
    localStorage.setItem(BpLayoutComponent.SIDEBAR_COLLAPSED_STORAGE_KEY, 'false');
  }

  /**
   * OPEN INTERCOM CHAT
   *
   * @param {MouseEvent} $event
   * @memberof BpLayoutComponent
   */
  openIntercomChat($event: MouseEvent) {
    $event.preventDefault();
    try {
      (window as any).Intercom('show');
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * NAVIGATE TO THE /dashboard
   *
   * @memberof BpLayoutComponent
   */
  goToDashboard() {
    this.router.navigate(['/dashboard']);
  }

  /**
   * Navigate back using browser history, with fallback to dashboard
   */
  goBack() {
    // La vista puede definir adónde lleva su flecha (el resumen de una meta va al inicio).
    if (this.bpHeaderService.handleBack()) {
      return;
    }
    // Check if there's history to go back to
    if (window.history.length > 1) {
      this.location.back();
    } else {
      // Fallback to dashboard if no history
      this.router.navigate(['/dashboard']);
    }
  }

  getRouteAnimationData() {
    return this.contexts.getContext('primary')?.route?.snapshot?.data?.['animation'];
  }

  /**
   * GET A FORMATED CLASS FOR MAKE FA-ICONS COMPATIBLE WITH FUSE NAV-BAR
   * (this fa-icons dont exits in fuse)
   *
   * @param {string} faIcon
   * @return {*}
   * @memberof BpLayoutComponent
   */
  formatFaIcon(faIcon: string) {
    let faIconClass = '';
    const faSplitIcon = faIcon.split('-');
    switch (faSplitIcon[0]) {
      case 'fab fa':
        faIconClass = 'bp-fa-icon ' + faIcon;
        break;
      case 'fa':
        faIconClass = 'bp-fa-icon fa ' + faIcon;
        break;
    }
    return faIconClass;
  }

  /**
   * RETURN THE EQUIVALENT FUSE ICON OF A FA-ICON OR THE SAME FA-ICON WITH A SPECIAL CLASS
   *
   * @param {string} [portalIcon]
   * @return {*}  {{ icon: string; classes: string }}
   * @memberof BpLayoutComponent
   */
  getIconOrClass(portalIcon?: string): { icon: string; classes: string } {
    if (!portalIcon) return { icon: 'mat_outline:help_outline', classes: '' };

    const fuseIcon = {
      'fa-baby': 'mat_outline:escalator_warning',
      'fa-user-graduate': 'heroicons_outline:academic-cap',
      'fa-piggy-bank': 'mat_outline:savings',
      'fa-gift': 'heroicons_outline:gift',
      'fa-home': 'heroicons_outline:home',
      'fa-umbrella': 'feather:umbrella',
      'fa-user-tie': 'heroicons_outline:briefcase',
      'fa-heart': 'heroicons_outline:heart',
      'fa-arrow-up': 'feather:arrow-up-circle',
      'fa-umbrella-beach': 'mat_solid:beach_access',
      'fa-arrow-circle-up': 'feather:arrow-up-circle',
      'fa-users': 'heroicons_outline:user-group',
      'fa-biking': 'mat_outline:directions_bike',
      'fa-plane-departure': 'mat_outline:flight_takeoff',
      'fa-graduation-cap': 'heroicons_outline:academic-cap',
      'fa-mountain': 'mat_outline:landscape',
      'fa-university': 'heroicons_outline:building-library',
    };

    if (Object.keys(fuseIcon).includes(portalIcon)) {
      return { icon: fuseIcon[portalIcon], classes: '' };
    } else {
      // Icon always has to have content
      // this icon is goin to has d-none
      return {
        icon: 'heroicons_solid:chevron-up',
        classes: this.formatFaIcon(portalIcon) || 'fa-regular fa-circle',
      };
    }
  }

  /**
   * SEARCH IN THE USER GOAL FOR GOAL WITH A category.code
   *
   * @param {string} category
   * @return {*}  {(GoalModel | undefined)}
   * @memberof BpLayoutComponent
   */
  searchGoalByCategory(
    category: string,
    strategy?: InvestmentStrategyModel,
  ): GoalModel | undefined {
    if (strategy) {
      return this.goals?.find(
        goal =>
          goal?.goalCategory?.code === category &&
          goal.portfolio?.investmentStrategyId === strategy.id,
      );
    } else {
      return this.goals?.find(goal => goal?.goalCategory?.code === category);
    }
  }

  /**
   * SEARCH IN THE USER GOAL FOR GOAL WITH A category.code and currency code
   *
   * @param {string} category
   * @param {string} currencyCode
   * @return {*}  {(GoalModel | undefined)}
   * @memberof BpLayoutComponent
   */
  searchGoalByCategoryAndCurrency(category: string, currencyCode: string): GoalModel | undefined {
    return this.goals?.find(
      goal => goal?.goalCategory?.code === category && goal.currency?.currencyCode === currencyCode,
    );
  }

  /**
   * SEARCH IN THE userFinancialEntities FOR A ENTITY WITH A uuid AND active
   *
   * @param {string} financialEntityUuid
   * @return {*}  {boolean}
   * @memberof BpLayoutComponent
   */
  searchIfUserHasFinancialEntity(financialEntityUuid: string): boolean {
    return this.user?.userFinancialEntities?.find(
      f => f.financialEntity?.uuid === financialEntityUuid && f.status === 'active',
    )
      ? true
      : false;
  }

  /**
   * Set lastRouteChildConfigData with the actual rooute data
   *
   * @memberof BpLayoutComponent
   */
  getLastRouteChildConfigData() {
    let lastRouteChild = this.activatedRoute.snapshot;

    while (lastRouteChild.firstChild) {
      lastRouteChild = lastRouteChild.firstChild;
    }

    this.lastRouteChildConfigData = lastRouteChild.routeConfig?.data;
  }
}
